import { useState } from 'react';
import Data from '../../repository';

const businessTypes = [
	{
		id: 'membership-mode',
		value: 'membership',
		label: 'Membership Mode',
		description: 'For any business using a membership or subscription model — gyms, clubs, studios, associations, and recurring service providers.'
	},
	{
		id: 'experience-mode',
		value: 'experiences',
		label: 'Experience Mode',
		description: 'For bookable experiences, activities, facilities, and venues — retreats, tours, venue hire, attractions, workshops and events.'
	},
	{
		id: 'services-staff',
		value: 'clinic',
		label: 'Services & Staff Booking',
		description: 'For businesses offering multiple services with different durations and pricing – salons, barbers, clinics, beauty therapists, wellness centres and similar.'
	},
	{
		id: 'classes-mode',
		value: 'experiences',
		label: 'Classes Mode',
		description: 'For gyms, yoga studios, fitness instructors, dance schools, tutors, music teachers, workshop hosts and similar businesses.'
	},
	{
		id: 'hybrid-gym',
		value: 'hybrid',
		label: 'Hybrid Gym',
		description: 'For gyms combining monthly memberships, token balances, open gym access slots, and classes.'
	},
	{
		id: 'hybrid-gym-new',
		value: 'hybrid-new',
		label: 'Hybrid Gym - Split Tokens',
		description: 'For gyms testing separate token balances for open gym bookings and class bookings.'
	},
	{
		id: 'facility-studio',
		value: 'sessions',
		label: 'Facility & Studio Booking',
		description: 'For gym studios, private training facilities, tanning salons, sunbed facilities, sports courts, and simulators using fixed time slots.'
	},
	{
		id: 'sports-club',
		value: 'membership',
		label: 'Sports Clubs',
		description: 'For football clubs, rugby clubs, martial arts academies, swimming clubs, and any other membership based sports organisations.'
	},
	{
		id: 'accommodation',
		value: 'restaurant',
		label: 'Accommodation & Camping',
		description: 'For hotels, guesthouses, campsites, glamping sites, and holiday parks.'
	},
	{
		id: 'crown-dependencies',
		value: 'restaurant',
		label: 'Crown Dependencies Mode',
		description: 'Designed specifically for businesses in the Isle of Man and Channel Islands.'
	},
	{
		id: 'other',
		value: 'other',
		label: 'Other',
		description: 'My business type is not listed above.'
	}
];

const defaultValues = {
	companyName: '',
	contactFirstName: '',
	contactLastName: '',
	contactEmail: '',
	fromEmail: '',
	contactPhone: '',
	businessTypeId: '',
	businessTypeOther: '',
	businessDetails: '',
	website: '',
	notes: ''
};

const labelStyle = {
	display: 'block',
	fontSize: '13px',
	fontWeight: 600,
	color: '#374151',
	marginBottom: '5px'
};

const inputStyle = {
	width: '100%',
	boxSizing: 'border-box'
};

function OnboardingCustomer() {
	const [form, setForm] = useState(defaultValues);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(false);

	const onChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const onSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setError(null);
		setSuccess(false);

		try {
			const selectedType = businessTypes.find((t) => t.id === form.businessTypeId);
			await Data.createOnboardingRequest({
				...form,
				businessType: selectedType?.value || ''
			});
			setSuccess(true);
			setForm(defaultValues);
		} catch (err) {
			setError(err.response?.data?.message || 'Unable to submit onboarding request.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '28px 16px' }}>
			<div style={{ maxWidth: '780px', margin: '0 auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '28px' }}>
				<h1 style={{ margin: 0, fontSize: '26px' }}>Customer Onboarding</h1>
				<p style={{ margin: '8px 0 0', color: '#6b7280' }}>
					Fill in the form below to sign up.
				</p>

				{error ? (
					<div style={{ marginTop: '20px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '6px', padding: '12px 14px' }}>
						{error}
					</div>
				) : null}

				<form onSubmit={onSubmit} style={{ marginTop: '24px', display: 'grid', gap: '18px' }}>

					{/* Company */}
					<div>
						<label style={labelStyle}>Company Name *</label>
						<input
							style={inputStyle}
							type="text"
							placeholder="e.g. Acme Fitness Ltd"
							value={form.companyName}
							onChange={(e) => onChange('companyName', e.target.value)}
							required
						/>
					</div>

					{/* Contact name */}
					<div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
						<div>
							<label style={labelStyle}>First Name *</label>
							<input
								style={inputStyle}
								type="text"
								placeholder="First name"
								value={form.contactFirstName}
								onChange={(e) => onChange('contactFirstName', e.target.value)}
								required
							/>
						</div>
						<div>
							<label style={labelStyle}>Last Name *</label>
							<input
								style={inputStyle}
								type="text"
								placeholder="Last name"
								value={form.contactLastName}
								onChange={(e) => onChange('contactLastName', e.target.value)}
								required
							/>
						</div>
					</div>

					{/* Emails */}
					<div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
						<div>
							<label style={labelStyle}>Contact Email *</label>
							<input
								style={inputStyle}
								type="email"
								placeholder="your@email.com"
								value={form.contactEmail}
								onChange={(e) => onChange('contactEmail', e.target.value)}
								required
							/>
						</div>
						<div>
							<label style={labelStyle}>From Email *</label>
							<input
								style={inputStyle}
								type="email"
								placeholder="noreply@yourdomain.com"
								value={form.fromEmail}
								onChange={(e) => onChange('fromEmail', e.target.value)}
								required
							/>
						</div>
					</div>

					{/* Phone */}
					<div>
						<label style={labelStyle}>Phone Number</label>
						<input
							style={inputStyle}
							type="text"
							placeholder="+44 7700 900000"
							value={form.contactPhone}
							onChange={(e) => onChange('contactPhone', e.target.value)}
						/>
					</div>

					{/* Business type */}
					<div>
						<label style={{ ...labelStyle, marginBottom: '10px' }}>Business Type *</label>
						<div style={{ display: 'grid', gap: '8px' }}>
							{businessTypes.map((type) => {
								const selected = form.businessTypeId === type.id;
								return (
									<div
										key={type.id}
										onClick={() => onChange('businessTypeId', type.id)}
										style={{
											border: `2px solid ${selected ? '#2563eb' : '#e5e7eb'}`,
											borderRadius: '8px',
											padding: '12px 14px',
											cursor: 'pointer',
											background: selected ? '#eff6ff' : '#fff',
											transition: 'border-color 0.15s, background 0.15s'
										}}
									>
										<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
											<div style={{
												width: '16px',
												height: '16px',
												borderRadius: '50%',
												border: `2px solid ${selected ? '#2563eb' : '#9ca3af'}`,
												background: selected ? '#2563eb' : '#fff',
												flexShrink: 0
											}} />
											<span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{type.label}</span>
										</div>
										<p style={{ margin: '6px 0 0 26px', fontSize: '13px', color: '#6b7280', lineHeight: '1.45' }}>
											{type.description}
										</p>
									</div>
								);
							})}
						</div>

						{form.businessTypeId === 'other' ? (
							<div style={{ marginTop: '10px' }}>
								<label style={labelStyle}>Please describe your business type *</label>
								<input
									style={inputStyle}
									type="text"
									placeholder="Describe your business type"
									value={form.businessTypeOther}
									onChange={(e) => onChange('businessTypeOther', e.target.value)}
									required
								/>
							</div>
						) : null}
					</div>

					{/* Business details */}
					<div>
						<label style={labelStyle}>Tell us about your business *</label>
						<textarea
							style={inputStyle}
							rows={4}
							placeholder="Briefly describe your business, what you offer, and how you currently manage bookings or memberships."
							value={form.businessDetails}
							onChange={(e) => onChange('businessDetails', e.target.value)}
							required
						/>
					</div>

					{/* Website */}
					<div>
						<label style={labelStyle}>Website</label>
						<input
							style={inputStyle}
							type="text"
							placeholder="https://yourwebsite.com"
							value={form.website}
							onChange={(e) => onChange('website', e.target.value)}
						/>
					</div>

					{/* Notes */}
					<div>
						<label style={labelStyle}>Additional Notes</label>
						<textarea
							style={inputStyle}
							rows={3}
							placeholder="Anything else you'd like us to know."
							value={form.notes}
							onChange={(e) => onChange('notes', e.target.value)}
						/>
					</div>

					<button type="submit" className="btn btnPrimary" disabled={loading}>
						{loading ? 'Submitting...' : 'Submit Request'}
					</button>
				</form>

				{success ? (
					<div style={{ marginTop: '20px', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: '6px', padding: '12px 14px' }}>
						Request submitted successfully. Our team will review and contact you shortly.
					</div>
				) : null}
			</div>
		</div>
	);
}

export default OnboardingCustomer;
